/**
 * Finalize flow extracted from TraversalStore.
 *
 * executeFinalize  — captcha gate → POST /finalize → redirect
 * executeGoToFinal — post-finalize redirect orchestration
 *
 * Both functions receive a deps object to avoid tight coupling to the store
 * class, making them individually testable.
 */
import { AxiosError, AxiosInstance, AxiosResponse } from 'axios'
import { when } from 'mobx'
import { RegisteredEventName } from '@finanzcheck/catalyst-pollard'
import FinalizeError from 'error/finalizeError'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { trackFinal, trackPreFinalize } from 'utils/tracking'
import { updateFfgConf } from 'startup/command/initFfgConf'
import { logInfo } from 'utils/log'
import isNotAffiliate from 'utils/formDependencies/conditions/isNotAffiliate'
import { getEnv } from 'utils/env'
import { handleCaptcha } from 'api/utils/handleCaptcha'
import { logAndTrackError } from 'utils/log/logAndTrackError'
import { Data, RootObject } from 'interfaces/ApolloResponse.interface'
import { LoadingMode } from 'components/LoadingProvider'
import {
  AmplitudeEvent,
  FinalRedirectStatus
} from 'utils/tracking/amplitude/amplitude.interface'
import {
  AffiliateEventName,
  redirectAffiliate,
  sendAffiliateEvent
} from 'utils/affiliate'
import RootStore from 'stores/RootStore'
import { postFinalizeTraversal } from './api'

export interface FinalizeDeps {
  rootStore: RootStore
  apolloClient: AxiosInstance
  data: Data
  traversalId: string
  isSmava: boolean
  /** Callback so the store can mark setIsGoToFinalSucceeded(true) */
  setIsGoToFinalSucceeded: (value: boolean) => void
}

/**
 * Runs the full finalize flow:
 *   1. Waits for captcha to complete
 *   2. POSTs to /finalize
 *   3. Calls executeGoToFinal on success
 */
export const executeFinalize = async (
  deps: FinalizeDeps,
  onError?: (
    error: Error | AxiosError<RootObject> | undefined,
    checkAllPages?: boolean
  ) => void,
  checkAllPages?: boolean
): Promise<void> => {
  const { rootStore, apolloClient, data, traversalId } = deps
  const { setLoadingMode, captcha } = rootStore
  const { directToOffers } = rootStore.urlParams

  /* tracks events that need to be fired before finalizing the traversal */
  trackPreFinalize(rootStore)

  try {
    await when(() => captcha.captchaFlowStatus !== 'idle', { timeout: 120000 })
    if (captcha.captchaFlowStatus !== 'aborted') {
      handleCaptcha({
        rootStore,
        captchaRef: rootStore.captcha.captchaRef,
        source: 'finalize'
      })
      await when(
        () =>
          captcha.captchaFlowStatus === 'success' ||
          captcha.captchaFlowStatus === 'error',
        { timeout: 120000 }
      )
    }
  } catch (err) {
    logAndTrackError(new Error('Captcha on final timeout or error'))
    trackInAmplitude(AmplitudeEvent.CaptchaTimeoutError, {
      final: true,
      error: String(err),
      errObject: err
    })
  }

  setLoadingMode(LoadingMode.Blocking)

  try {
    const finalResponse = await postFinalizeTraversal(
      apolloClient,
      data.system.advertisementId,
      traversalId,
      data
    )

    rootStore.fieldErrors.setIsFinalizeError(false)
    await executeGoToFinal(deps, finalResponse)
  } catch (error) {
    await trackInAmplitude(AmplitudeEvent.GoToFinal, {
      status: FinalRedirectStatus.Failed,
      directToOffers: directToOffers ? 'true' : 'false'
    })
    setLoadingMode(LoadingMode.None)
    rootStore.fieldErrors.setIsFinalizeError(true)

    if (!onError) {
      throw error
    }

    onError(error as AxiosError, checkAllPages)
  }
}

/**
 * Handles the redirect after a successful finalize response.
 * Fires tracking events, builds the redirect URL, and navigates.
 */
export const executeGoToFinal = async (
  deps: FinalizeDeps,
  response: AxiosResponse<{ result: { uri: Location } }>
): Promise<void> => {
  const { rootStore, isSmava, setIsGoToFinalSucceeded } = deps
  const {
    data: { result }
  } = response

  if (!result || !result.uri) {
    throw new FinalizeError(
      'Could not redirect to final. URI missing!',
      response.config,
      response
    )
  }

  const { directToOffers } = rootStore.urlParams

  try {
    trackFinal(rootStore)
    // sendAffiliateEvent expects the traversal store — pass rootStore.traversal
    sendAffiliateEvent(
      AffiliateEventName.FinalConversion,
      rootStore.traversal,
      rootStore
    )
    updateFfgConf('step', 'final')

    const {
      traversal: {
        data: {
          debtors: { count },
          system: { advertisementId }
        }
      }
    } = rootStore

    await trackInAmplitude(AmplitudeEvent.GoToFinal, {
      status: FinalRedirectStatus.Succeeded,
      advertisementId,
      debtorCount: count,
      directToOffers: directToOffers ? 'true' : 'false'
    })
  } catch (error) {
    logAndTrackError(error as AxiosError)
  } finally {
    const palette = rootStore.urlParams.palette
      ? `${result.uri.search ? '&' : '?'}palette=${encodeURIComponent(
          rootStore.urlParams.palette
        )}`
      : undefined

    const finalUrl = `${result.uri}${palette || ''}&context=${
      rootStore.urlParams?.context
    }`
    logInfo(`[goToFinal] Redirect to ${finalUrl}`)

    setIsGoToFinalSucceeded(true)

    const notAffiliate = isNotAffiliate(rootStore.urlParams?.context)

    const queryParams = finalUrl.split('?')[1]
    const redirectUrl = `${getEnv('REACT_APP_OFFER_PAGE_URL')}/?${queryParams}${
      palette || ''
    }`

    if (!notAffiliate) {
      if (
        rootStore.urlParams?.affiliateLinkout === 'true' ||
        rootStore.urlParams?.embedded !== 'true'
      ) {
        rootStore.setLoadingMode(LoadingMode.None)
        redirectAffiliate(redirectUrl, RegisteredEventName.redirect)
        return
      }

      window.setTimeout(
        () => {
          window.location.replace(finalUrl)
        },
        isSmava ? 500 : 0
      )

      return
    }

    redirectAffiliate(redirectUrl, RegisteredEventName.redirect)
    rootStore.setLoadingMode(LoadingMode.None)
  }
}
